// Federal servicers live on studentaid.gov subdomains, which the .gov rule already covers.
const TRUSTED_DOMAINS = [
  'tuition.io',
  'aidvantage.com',
  'ecmc.org',
  'edfinancial.com',
  'kheaa.com',
  'kheslc.com',
  'mohela.com',
  'navient.com',
  'nelnet.com',
  'nelnet.net',
  'salliemae.com',
];
const TRUSTED_TLDS = ['gov'];
const MAIL_PARAMS = ['subject', 'body'];
const MAIL_DOMAIN = /^[a-z\d.-]+$/i;

const isTrustedHost = (hostname: string) => {
  const host = hostname.toLowerCase().replace(/\.$/, '');
  return (
    TRUSTED_DOMAINS.some((domain) => host === domain || host.endsWith(`.${domain}`)) ||
    TRUSTED_TLDS.some((tld) => host.endsWith(`.${tld}`))
  );
};

const isTrustedMailto = (url: URL) => {
  if ([...url.searchParams.keys()].some((key) => !MAIL_PARAMS.includes(key.toLowerCase()))) {
    return false;
  }
  let recipients: string[];
  try {
    recipients = decodeURIComponent(url.pathname).split(',');
  } catch {
    return false;
  }
  return recipients.every((recipient) => {
    const [, domain, ...rest] = recipient.trim().split('@');
    return (
      domain !== undefined && rest.length === 0 && MAIL_DOMAIN.test(domain) && isTrustedHost(domain)
    );
  });
};

/**
 * Resolves a chat markdown link and decides whether it may render as a live anchor.
 * Same-origin and relative links, `tuition.io` and its subdomains, any `.gov` host, and
 * known loan servicer hosts pass; `mailto:` passes only when every recipient is on one of
 * those domains and it carries no params beyond `subject` and `body`. Expects an href that already passed the scheme check.
 *
 * @param href link destination as written in the markdown
 * @param base URL relative hrefs resolve against; defaults to the document's base URI
 * @returns the resolved URL, serialized, or `null` when the destination is not trusted or
 *   does not parse
 */
export function trustedChatLinkHref(href: string, base = document.baseURI) {
  let url: URL;
  try {
    url = new URL(href, base);
  } catch {
    return null;
  }

  if (url.protocol === 'mailto:') return isTrustedMailto(url) ? url.href : null;
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
  if (url.origin === new URL(base).origin || isTrustedHost(url.hostname)) return url.href;
  return null;
}
